import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { DOCUMENT_TYPE } from '@/features/documents/constants';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@/features/auth';
import { useMemo, useState } from 'react';
import { FilesPageTypeFilter } from '@/features/documents/components/files-page/files-page-type-filter';
import { Breadcrumb, BreadcrumbLink, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton } from '@/ui/components/header';
import { Icon } from '@/ui/components/icon';
import { AiBrain02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { tenderFeedbackQuery } from '@/features/tender-feedbacks/queries';
import { NotFound } from '@/ui/components/not-found';
import { useQuery } from '@tanstack/react-query';
import type { TenderFeedbackDocument } from '@/features/tender-feedbacks/types';
import { Badge } from '@/ui/components/badge';
import { FileIcon } from '@/ui/components/file-icon';
import { Spinner } from '@/ui/components/spinner';
import {
  MainActions,
  MainActionsEnd,
  MainActionsSearchInput,
  MainHeader,
  MainHeaderBackButton,
  MainScrollArea,
} from '@/ui/components/main';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/ui/components/tooltip';
import { useDocumentPreview } from '@/features/documents/preview/helpers';
import { useDebouncedQuery } from '@/ui/hooks/use-debounced-query';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/ui/components/empty';
import { Icons } from '@/ui/components/icons';
import { Button } from '@/ui/components/button';
import { H1 } from '@/ui/components/heading';
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Table, TableBody, TableRow } from '@/ui/components/table';
import { DataTableHeader, DataTableCell } from '@/ui/components/data-table';

export const Route = createFileRoute(
  '/_authed/settings/knowledge-base/tender-feedbacks/$tenderFeedbackId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { tenderFeedbackId } = Route.useParams();
  const auth = useAuth();
  const organizationId = auth.user.organizationId;

  const openPreview = useDocumentPreview();
  const { searchQuery, setSearchQuery, debouncedSearchQuery } = useDebouncedQuery();
  const [documentType, setDocumentType] = useState<string | undefined>(undefined);

  const { data, isLoading } = useQuery(tenderFeedbackQuery(organizationId, tenderFeedbackId));
  const allDocuments = data?.documents ?? [];

  const filterOptions = [
    {
      label: t({ en: 'Tender package documents', nb: 'Anbudspakkedokumenter' }),
      value: String(DOCUMENT_TYPE.tenderPackage),
    },
    {
      label: t({ en: 'Bid documents', nb: 'Tilbudsdokumenter' }),
      value: String(DOCUMENT_TYPE.bid),
    },
    {
      label: t({ en: 'Feedback documents', nb: 'Tilbakemeldingsdokumenter' }),
      value: String(DOCUMENT_TYPE.tenderFeedback),
    },
  ];
  const filterCounts = Object.fromEntries(
    filterOptions.map((option) => [
      option.value,
      allDocuments.filter((doc) => String(doc.documentType) === option.value).length,
    ]),
  );

  const documents = useMemo(
    () =>
      allDocuments.filter((doc) => {
        const name = doc.documentFilename.toLowerCase();
        if (debouncedSearchQuery && !name.includes(debouncedSearchQuery.toLowerCase()))
          return false;
        if (documentType && String(doc.documentType) !== documentType) return false;
        return true;
      }),
    [allDocuments, debouncedSearchQuery, documentType],
  );

  const columns = useMemo<ColumnDef<TenderFeedbackDocument>[]>(
    () => [
      {
        accessorKey: 'documentFilename',
        size: 200,
        meta: { primary: true },
        header: () => t({ en: 'Name', nb: 'Navn' }),
        cell: ({ row }) => (
          <div className="flex items-center gap-2 min-w-50">
            <Badge kind="icon" size="sm" className="shrink-0">
              <FileIcon fileName={row.original.documentFilename} />
            </Badge>
            <Tooltip>
              <TooltipTrigger className="line-clamp-1 cursor-pointer">
                {row.original.documentFilename}
              </TooltipTrigger>
              <TooltipPopup>{row.original.documentFilename}</TooltipPopup>
            </Tooltip>
          </div>
        ),
      },
      {
        accessorKey: 'sizeInMegabytes',
        header: () => t({ en: 'Size', nb: 'Størrelse' }),
        cell: ({ row }) =>
          row.original.sizeInMegabytes ? `${row.original.sizeInMegabytes.toFixed(2)} MB` : '-',
        size: 65,
      },
    ],
    [],
  );

  const table = useReactTable({
    columns,
    data: documents,
    getCoreRowModel: getCoreRowModel(),
  });
  const totalCount = allDocuments.length;

  if (isLoading) return <Spinner kind="inset" />;
  if (!data) return <NotFound />;

  return (
    <>
      <Header>
        <HeaderBackButton to="/settings/knowledge-base" search={{ tab: 'tender_feedbacks' }} />
        <Breadcrumb>
          <BreadcrumbLink to="/settings/knowledge-base" search={{ tab: 'tender_feedbacks' }}>
            <Icon icon={AiBrain02Icon} />
            {t({ en: 'Knowledge base', nb: 'Kunnskapsbase' })}
          </BreadcrumbLink>
          <BreadcrumbPage>{data.tenderName}</BreadcrumbPage>
        </Breadcrumb>
      </Header>
      <MainHeader>
        <H1 className="capitalize">
          <MainHeaderBackButton
            to="/settings/knowledge-base"
            search={{ tab: 'tender_feedbacks' }}
          />
          {t(common.files)}{' '}
          <span className="ml-1.5 text-muted text-2xl">
            {totalCount === 0 ? null : `(${totalCount})`}
          </span>
        </H1>
      </MainHeader>
      <MainActions className="border-t!">
        <MainActionsEnd>
          <MainActionsSearchInput
            onChange={(e) => setSearchQuery(e.target.value)}
            value={searchQuery}
          />
        </MainActionsEnd>
      </MainActions>
      <FilesPageTypeFilter
        filterOptions={filterOptions}
        documentType={documentType}
        onDocumentTypeChange={setDocumentType}
        totalCount={allDocuments.length}
        filterCounts={filterCounts}
      />
      <MainScrollArea>
        {documents.length > 0 ? (
          <Table>
            <DataTableHeader table={table} />
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer"
                  onClick={() =>
                    openPreview({
                      documentId: row.original.docId,
                      title: row.original.documentFilename,
                    })
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <DataTableCell key={cell.id} cell={cell} />
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Empty>
            <EmptyMedia>
              <Icons.files />
            </EmptyMedia>
            <EmptyContent>
              <EmptyTitle>{t(common.files)}</EmptyTitle>
              <EmptyDescription>
                {debouncedSearchQuery || documentType
                  ? t(common.filters.noResults)
                  : t({ en: 'No documents found', nb: 'Ingen dokumenter funnet' })}
              </EmptyDescription>
              {(debouncedSearchQuery || documentType) && (
                <Button
                  onClick={() => {
                    setSearchQuery('');
                    setDocumentType(undefined);
                  }}
                  variant="secondary"
                >
                  {t(common.filters.resetFilters)}
                </Button>
              )}
            </EmptyContent>
          </Empty>
        )}
      </MainScrollArea>
    </>
  );
}
